/**
 * CapabilityConfigPage — Visual capability registry + role→capability assignment matrix
 *
 * Displays all registered capabilities grouped by domain,
 * shows which roles have which capabilities, and allows editing grants.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Shield, Truck, Package, Ship, DollarSign, Settings2,
  Loader2, RefreshCw, Search, Monitor,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CapabilityDef {
  id: string;
  domain: string;
  label: string;
  description: string;
  actions: string[];
  requiresApproval?: boolean;
}

interface RoleInfo {
  id: string;
  name: string;
  permissions: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DOMAIN_ICONS: Record<string, React.ElementType> = {
  tms: Truck,
  wms: Package,
  fms: Ship,
  finance: DollarSign,
  admin: Settings2,
  platform: Monitor,
};

const DOMAIN_LABELS: Record<string, string> = {
  tms: 'Transport',
  wms: 'Warehouse',
  fms: 'Freight',
  finance: 'Finance',
  admin: 'Administration',
  platform: 'Platform',
};

const ACTION_COLORS: Record<string, string> = {
  view:    'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  create:  'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  update:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  delete:  'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  execute: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  approve: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function CapabilityConfigPage() {
  const [capabilities, setCapabilities] = useState<CapabilityDef[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('registry');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load capabilities from capability API endpoint
      const capRes = await api.get<{ items?: CapabilityDef[] }>('/capability/definitions');
      setCapabilities(capRes?.items ?? []);

      // Load roles
      const roleRes = await api.get<{ items?: RoleInfo[] }>('/entities/roles?limit=100');
      const rawRoles = (roleRes?.items ?? []) as unknown as Record<string, unknown>[];
      setRoles(rawRoles.map((r) => ({
        id: (r.id as string) ?? '',
        name: (r.name as string) ?? (r.slug as string) ?? '',
        permissions: Array.isArray(r.permissions) ? (r.permissions as string[]) : [],
      })));
    } catch (e) {
      setCapabilities([]);
      setRoles([]);
      setError(e instanceof Error ? e.message : 'Failed to load capabilities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group capabilities by domain
  const grouped = capabilities.reduce<Record<string, CapabilityDef[]>>((acc, cap) => {
    (acc[cap.domain] ??= []).push(cap);
    return acc;
  }, {});

  const q = search.toLowerCase();
  const filteredDomains = Object.entries(grouped).filter(([domain, caps]) =>
    !q || domain.includes(q) || caps.some(c => c.label.toLowerCase().includes(q) || c.id.includes(q)),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Capability Registry</h2>
          <p className="text-xs text-muted-foreground">
            Platform capabilities, role assignments, and action permissions
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search…" className="pl-8 h-8 w-44 text-xs"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-8 text-xs">
          <TabsTrigger value="registry">
            <Shield className="h-3 w-3 mr-1" /> Registry ({capabilities.length})
          </TabsTrigger>
          <TabsTrigger value="matrix">
            Matrix
          </TabsTrigger>
        </TabsList>

        {/* ── Registry Tab ── */}
        <TabsContent value="registry">
          {error && (
            <div className="mb-4 px-4 py-3 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-300">Capability API not available</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{error}</p>
              <p className="text-xs text-muted-foreground mt-1">
                This feature requires the capability definitions API to be registered in platform-api.
                Capabilities are defined via the policy engine.
              </p>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredDomains.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No capabilities registered</p>
                <p className="text-xs mt-1">Capabilities are defined in the policy engine package</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredDomains.map(([domain, caps]) => {
                const Icon = DOMAIN_ICONS[domain] ?? Shield;
                const label = DOMAIN_LABELS[domain] ?? domain;
                const filtered = q ? caps.filter(c => c.label.toLowerCase().includes(q) || c.id.includes(q)) : caps;

                return (
                  <Card key={domain}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {label}
                        <Badge variant="secondary" className="text-[10px]">{caps.length}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="space-y-2">
                        {filtered.map((cap) => (
                          <div key={cap.id} className="flex items-start justify-between py-1.5 border-b last:border-0">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium">{cap.label}</span>
                                <code className="text-[10px] text-muted-foreground font-mono">{cap.id}</code>
                                {cap.requiresApproval && (
                                  <Badge variant="outline" className="text-[9px] border-orange-300 text-orange-600">
                                    Approval Required
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground">{cap.description}</p>
                            </div>
                            <div className="flex gap-1 flex-wrap justify-end">
                              {cap.actions.map((a) => (
                                <span key={a} className={`text-[9px] px-1.5 py-0.5 rounded ${ACTION_COLORS[a] ?? 'bg-gray-100 text-gray-700'}`}>
                                  {a}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Matrix Tab ── */}
        <TabsContent value="matrix">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : roles.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                <p>No roles found</p>
                <p className="text-xs mt-1">Define roles in Admin → Users to see the capability matrix</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left py-2 px-3 font-medium sticky left-0 bg-background z-10">
                          Capability
                        </th>
                        {roles.map((r) => (
                          <th key={r.id} className="text-center py-2 px-2 font-medium min-w-[80px]">
                            {r.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {capabilities.map((cap) => (
                        <tr key={cap.id} className="border-b hover:bg-muted/20">
                          <td className="py-1.5 px-3 sticky left-0 bg-background z-10">
                            <span className="font-medium">{cap.label}</span>
                            <span className="text-muted-foreground ml-1">({cap.domain})</span>
                          </td>
                          {roles.map((r) => {
                            const hasIt = r.permissions.includes(cap.id) ||
                              r.permissions.includes(`${cap.domain}:*`) ||
                              r.permissions.includes('*');
                            return (
                              <td key={r.id} className="text-center py-1.5 px-2">
                                {hasIt ? (
                                  <span className="inline-block w-4 h-4 rounded bg-green-500/20 text-green-600">✓</span>
                                ) : (
                                  <span className="inline-block w-4 h-4 rounded text-muted-foreground/30">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
